import { createAsyncThunk } from "@reduxjs/toolkit";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import {
  createResearchRun,
  getEventLog,
  getResearchResult,
} from "../../../services/api/instances/researchApi/api";
import type {
  ResearchValue,
  TEventMessages,
  TRequestTasksRun,
  TResearchResult,
} from "../../../services/api/instances/researchApi/types";
import { RootState } from "../../../services/redux/types";
import { mergeEventMessages } from "../utils/mergeEventMessages";
import { getError } from "../../../services/lib/getError";

export const createResearchAction = createAsyncThunk(
  "research/createResearch",
  async (postObject: TRequestTasksRun, ThunkAPI) => {
    const { selectedId, data } = (ThunkAPI.getState() as RootState)
      .storageObjectState.research;
    const object = data.entities[selectedId as string];
    try {
      const run = await createResearchRun(postObject);

      if (!run || !object || !object.value) {
        throw new Error();
      }

      const updatedObject: ResearchValue = {
        research_object: {
          result: {
            basis: [],
            content: `# ${postObject.input} \n\n`,
            type: "",
          },
          event_log: {
            messages: [],
            progress_meter: 0,
            source_stats: null,
          },
          run_id: run.run_id,
        },
      };

      await ThunkAPI.dispatch(
        editStorageObject({
          id: object.id,
          name: object.friendly_name,
          value: updatedObject,
          service: "research",
        }),
      ).unwrap();

      return updatedObject;
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getEventLogAction = createAsyncThunk(
  "research/getEventLog",
  async (runId: string, ThunkAPI) => {
    const { researchValue } = (ThunkAPI.getState() as RootState).researchState;
    const { selectedId, data } = (ThunkAPI.getState() as RootState)
      .storageObjectState.research;
    try {
      const events = await getEventLog(runId);

      if (!events || !selectedId || !researchValue?.research_object.event_log) {
        throw new Error();
      }
      const selectedObject = data.entities[selectedId];
      const { messages, progress_meter, source_stats } =
        researchValue?.research_object?.event_log;
      const eventsMessages: TEventMessages[] = events.flatMap((event) => {
        if (event.data.message) {
          return {
            timestamp: event.data.timestamp as string,
            message: event.data.message,
          };
        }
        return [];
      });
      const mergedMessages = mergeEventMessages(messages, eventsMessages);
      const progressInfo = events.findLast(
        (event) => event.data.progress_meter,
      );
      let progress = progressInfo?.data.progress_meter
        ? progressInfo?.data.progress_meter
        : (progress_meter ?? 0);
      let sources = progressInfo?.data.source_stats
        ? progressInfo?.data.source_stats
        : (source_stats ?? {
            num_sources_considered: 0,
            num_sources_read: 0,
            sources_read_sample: [],
          });

      const updatedValue: ResearchValue = {
        research_object: {
          ...researchValue.research_object,
          event_log: {
            messages: mergedMessages,
            progress_meter: progress,
            source_stats: sources,
          },
        },
      };

      if (
        mergedMessages.length === messages.length &&
        progress_meter === progress &&
        source_stats?.num_sources_considered ===
          sources?.num_sources_considered &&
        source_stats?.num_sources_read === sources?.num_sources_read
      ) {
        return;
      }

      ThunkAPI.dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "research",
          skipUpdateListener: true,
        }),
      );
      return { updatedValue, selectedId };
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getResearchResultAction = createAsyncThunk(
  "research/getResearchResult",
  async (runId: string, ThunkAPI) => {
    const { researchValue } = (ThunkAPI.getState() as RootState).researchState;
    const { selectedId, data } = (ThunkAPI.getState() as RootState)
      .storageObjectState.research;

    if (!selectedId || !researchValue?.research_object) return;
    try {
      const oldContent = researchValue?.research_object?.result?.content ?? "";
      const selectedObject = data.entities[selectedId];
      const resultContent = await getResearchResult(runId);

      const researchResult: TResearchResult = {
        ...resultContent,
        content: oldContent.concat(resultContent.content),
      };

      const updatedValue: ResearchValue = {
        research_object: {
          ...researchValue?.research_object,
          event_log: {
            messages: researchValue?.research_object?.event_log?.messages ?? [],
            source_stats:
              researchValue?.research_object?.event_log?.source_stats ?? null,
            progress_meter: 100,
          },
          result: researchResult,
          run_id: null,
        },
      };

      ThunkAPI.dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: updatedValue as ResearchValue,
          service: "research",
          skipUpdateListener: true,
        }),
      );

      return { updatedValue, selectedId };
    } catch (error: unknown) {
      const message = getError(error);
      return ThunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
